import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, take } from 'rxjs';

/**
 * Dialog Focus Isolation
 *
 * Angular Material applies `inert` automatically on the siblings of
 * `cdk-overlay-container` when a dialog opens, so focus can't escape into the
 * background page. That logic assumes the container lives directly under
 * `<body>`.
 *
 * This app uses {@link AppCdkOverlayContainer} to transplant the container
 * into `.main-app-content` (for workspace isolation + AI sidebar coexistence).
 * After the transplant, Material's automatic inert no longer covers the page
 * shell — Tab can leak into the sidebar, list rows, and any other focusable
 * elements in the background while a dialog is open.
 *
 * This service bridges that gap:
 *  - Listens to `MatDialog.afterOpened` (dialog-specific API, not the generic
 *    OverlayContainer attach — so menus, selects, autocompletes, tooltips, and
 *    snack bars are NOT affected).
 *  - Maintains a counter for stacked dialogs (e.g. confirm-on-top-of-form).
 *  - On the first open, marks every sibling of `cdk-overlay-container` with
 *    the `inert` HTML attribute. Browsers natively remove inert subtrees from
 *    the tab order, suppress pointer events, and hide them from assistive tech.
 *  - Releases `inert` on the LAST dialog's `beforeClosed` — BEFORE Material's
 *    focus-restore runs, so the trigger element is reachable again and focus
 *    returns to it (config: `restoreFocus: true`).
 *
 */
@Injectable({ providedIn: 'root' })
export class DialogFocusIsolationService implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly doc = inject(DOCUMENT);

  private openCount = 0;
  private inertedElements: HTMLElement[] = [];
  private subscriptions: Subscription[] = [];

  init(): void {
    if (this.subscriptions.length) return;
    this.subscriptions.push(
      this.dialog.afterOpened.subscribe(ref => {
        this.onDialogOpened();
        // beforeClosed fires before Material's focus-restore — release inert
        // here so the trigger element can receive focus again.
        ref
          .beforeClosed()
          .pipe(take(1))
          .subscribe(() => this.onDialogClosing());
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    this.releaseInert();
  }

  private onDialogOpened(): void {
    this.openCount++;
    if (this.openCount === 1) this.applyInert();
  }

  private onDialogClosing(): void {
    this.openCount = Math.max(0, this.openCount - 1);
    if (this.openCount === 0) this.releaseInert();
  }

  private applyInert(): void {
    const overlay = this.doc.querySelector<HTMLElement>('.cdk-overlay-container');
    const parent = overlay?.parentElement;
    if (!parent) return;
    for (const child of Array.from(parent.children) as HTMLElement[]) {
      if (child === overlay || child.hasAttribute('inert')) continue;
      child.setAttribute('inert', '');
      this.inertedElements.push(child);
    }
  }

  private releaseInert(): void {
    for (const el of this.inertedElements) el.removeAttribute('inert');
    this.inertedElements = [];
  }
}
